"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "~/lib/utils";
import Link from "next/link";
import ArrowSmRightIcon from "@/icons/arrows/arrow-right-2.svg";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { GtmEvent } from "~/lib/gtm/getGtmEventData";
import type { ButtonHTMLAttributes, ReactNode, MouseEvent } from "react";

const buttonVariants = cva(
    [
        "flex items-center justify-center rounded-full font-medium uppercase leading-[100%] transition-all duration-300 ease-in-out leading-[1.3em] md:leading-[25px] font-medium tracking-[0.08em] md:tracking-[1.3px] font-platform",
        "hover:bg-activePeach hover:border-activePeach hover:text-trueBlack group",
        "disabled:opacity-50 disabled:cursor-not-allowed",      
    ],
    {
        variants: {
            variant: {
                default: "bg-heroBlue text-pureWhite border border-heroBlue disabled:hover:bg-softBlue disabled:hover:border-softBlue disabled:hover:text-heroBlue",
                secondary: "bg-pureWhite border border-transparent text-heroBlue disabled:hover:bg-pureWhite disabled:hover:text-heroBlue",
                accent: "bg-heroBlue text-pureWhite border border-transparent disabled:hover:bg-heroBlue disabled:hover:text-pureWhite",
                active: "bg-activePeach text-black border border-transparent disabled:hover:bg-activePeach disabled:hover:text-black",
                outline: "border-[2px] text-pureWhite",
            },
            outlineColor: {
                white: "border-pureWhite text-pureWhite",
                black: "border-trueBlack text-trueBlack",
            },
            size: {
                sm: "px-6 py-3 h-[48px] text-[12px] md:text-[12px] tracking-[1.2px]",
                md: "px-6 py-3 h-[52px] text-[16px] tracking-[1.3px]",
                lg: "px-6 py-3 h-[60px] text-[16px] tracking-[1.3px]",
            },
            hasArrow: {
                true: "py-4",
                false: "py-3",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "md",
            hasArrow: false,
        },
    }
);

export interface ButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    icon?: ReactNode;
    href?: string;
    gtmEventData?: GtmEvent;
    preventDefault?: boolean;
    handleClick?: () => void;
    target?: string;
    text?: string;
    hasArrow?: boolean;
}

export function Button({
    className,
    variant,
    hasArrow = false,
    preventDefault,
    size,
    gtmEventData,
    text,
    target,
    handleClick,
    href,
    outlineColor,
    disabled,
    ...props
}: ButtonProps) {
    const sendEvent = useTrackEvent();

    const classes = cn(
        buttonVariants({ variant, size, hasArrow, outlineColor }),
        className
    );

    let arrowColorClass = "text-pureWhite";

    if (variant === "outline") {
        arrowColorClass = outlineColor === "black" ? "text-trueBlack" : "text-pureWhite";
    } else if (variant === "secondary") {
        arrowColorClass = "text-heroBlue";
    }

    const handleOnClick = (event: MouseEvent<HTMLElement>) => {
        if (disabled) {
            event.preventDefault();
            return;
        }

        if (preventDefault) {
            event.preventDefault();
        }

        sendEvent(gtmEventData, href, text);

        if (typeof handleClick === "function") {
            handleClick();
        }
    };

    const content = (
        <>
            {text}
            {hasArrow && (
                <ArrowSmRightIcon
                    className={cn(
                        "group-hover:text-trueBlack transition duration-300 ease-in-out",
                        arrowColorClass
                    )}
                />
            )}
        </>
    );

    if (href) {
        const isExternal = href.startsWith("http");

        if (isExternal) {
            return (
                <a
                    href={disabled ? undefined : href}
                    target={target}
                    onClick={handleOnClick}
                    className={classes}
                    aria-disabled={disabled}
                >
                    {content}
                </a>
            );
        }

        return (
            <Link 
                href={disabled ? "#" : href} 
                className={classes} 
                onClick={handleOnClick}
                aria-disabled={disabled}
            >
                {content}
            </Link>
        );
    }

    return (
        <button 
            className={classes} 
            onClick={handleOnClick} 
            disabled={disabled}
            {...props}
        >
            {content}
        </button>
    );
}
